<template>
  <div class="app-details">
    <div class="details-input-section">
      <h3>App details</h3>
      <simplebar class="input-form" data-simplebar-auto-hide="false">
        <InputField
          v-model="appName"
          :note="'The name of your application on the device.'"
          :title="'Application Name'"
          class="form-field"
        />
        <InputField
          v-model="appNameInCode"
          :placeholder="packageNamePlaceholder"
          :defaultValueNote="'Default package name: '+ packageNamePlaceholder"
          :note="'The name of your package in code. Flutter packages uses all lowercase with underscores instead of spaces.'"
          :title="'Flutter package name'"
          class="form-field"
        />
        <InputField
          v-model="projectId"
          :placeholder="androidProjectIdPlaceholder"
          :note="'The Package Name (Android) / Bundle Id (iOS) for your application.' "
          :defaultValueNote="'Android package id: '+ androidProjectIdPlaceholder"
          :additionalNote="'iOS Bundle id: '+ iosProjectIdPlaceholder"
          :title="'Project ID'"
          class="form-field"
        />
        <InputField
          v-model="getSelectedTechnologyName"
          :disabled="1"
          :title="'Selected Framework'"
          class="form-field"
        />
        <InputField
          v-model="getSelectedArchitectureName"
          :disabled="1"
          :title="'Selected Architecture'"
          class="form-field"
        />
        <div class="extra-space"/>
      </simplebar>
    </div>

    <div class="views-section" v-on:click="resetSelectedView()">
      <h3>App Views</h3>
      <simplebar class="app-view-simplebar" data-simplebar-auto-hide="true">
        <div v-show="!hasViews" class="no-views-message">You need atleast one view to create an app.</div>
        <div class="app-views" v-show="hasViews">
          <div
            v-for="(view, index) in views"
            v-bind:key="index"
            class="view-representation"
            v-on:click.stop="setSelectedView(index)"
          >
            <div :class="{'selected-view' : isViewSelected(view)}" class="phone-representation">
              <img
                v-on:click.stop="removeView(index)"
                class="close-button"
                src="../assets/images/close-button.svg"
              >
              <ul>
                <li class="view-tag" v-show="view.start">Starting</li>
                <li class="view-tag" v-show="view.showTemplateUi">Template UI</li>
              </ul>
            </div>
            <p class="view-name">{{ view.name }}</p>
            <p class="view-type">{{ view.type }}</p>
          </div>
        </div>
      </simplebar>
    </div>

    <ViewInputForm
      class="views-input-section"
      :showForm="showViewForm"
      v-on:toggleForm="toggleShowForm()"
    />
    <button v-show="canGenerateApp" class="bottom-right" @click="generateApp()">Generate App</button>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations, mapState } from "vuex";
import ViewInputForm from "@/components/ViewInputForm.vue";
import InputField from "@/components/InputField.vue";
import firebase from "firebase";
import simplebar from "simplebar-vue";
import "simplebar/dist/simplebar.min.css";

export default {
  name: "AppDetails",
  components: {
    simplebar,
    InputField,
    ViewInputForm
  },
  mounted() {
    if (this.hasSelectedApp) {
      this.appName = this.selectedApp.appName;
      this.appNameInCode = this.selectedApp.packageName;
      this.projectId = this.selectedApp.projectId;
    }

    this.setFooterState(1);

    var canShowView = this.getSelectedArchitectureValue &&
        this.getSelectedArchitectureName &&
        this.getSelectedTechnologyName;
        
    if(!canShowView){
      this.$router.replace("select-technology");
    }
  },
  computed: {
    ...mapState(["views", "apps", "selectedApp", "userVerified"]),
    ...mapGetters([
      "getSelectedTechnologyName",
      "getSelectedArchitectureName",
      "getSelectedArchitectureValue",
      "isSelectedView",
      "hasSelectedApp"
    ]),
    hasViews() {
      return this.views.length > 0;
    },
    canGenerateApp() {
      return (
        this.appName &&
        this.getSelectedArchitectureValue &&
        this.getSelectedArchitectureName &&
        this.getSelectedTechnologyName &&
        this.hasViews &&
        (this.userVerified || (!this.userVerified && this.apps.length < 1))
      );
    },
    packageNamePlaceholder() {
      return this.appName.replace(/ /g, "_").toLowerCase();
    },
    packageNameiOSPlaceholder() {
      return this.appName
        .replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
          return index == 0 ? word.toLowerCase() : word.toUpperCase();
        })
        .replace(/\s+/g, "");
    },
    androidProjectIdPlaceholder() {
      var packageNameToShow =
        this.appNameInCode != ""
          ? this.appNameInCode
          : this.packageNamePlaceholder;
      return "com.appskeletons." + packageNameToShow;
    },
    iosProjectIdPlaceholder() {
      var packageNameToShow =
        this.appNameInCode != ""
          ? this.appNameInCode
          : this.packageNameiOSPlaceholder;
      return "com.appskeletons." + packageNameToShow;
    }
  },
  methods: {
    resetSelectedView() {
      this.CLEAR_SELECTED_VIEW();
    },
    setSelectedView(viewIndex) {
      this.SET_SELECTED_APP_VIEW(viewIndex);
      this.showViewForm = true;
    },
    isViewSelected(view) {
      var isSelected = this.isSelectedView(view);
      // console.log(`Is view selected. ${isSelected} ${JSON.stringify(view)}`);
      return isSelected;
    },
    generateApp() {
      // console.log("#### UserId: " + firebase.auth().currentUser.uid);

      for (let index = 0; index < this.views.length; index++) {
        this.views[index].name += "View";
      }

      var templateInfo = {
        userId: firebase.auth().currentUser.email,
        name: this.getSelectedArchitectureValue,
        appName: this.appName,
        appNameInCode:
          this.appNameInCode != ""
            ? this.appNameInCode
            : this.packageNamePlaceholder,
        appDescription: "This app is generated with AppSkeletons",
        projectId:
          this.projectId != ""
            ? this.projectId
            : this.androidProjectIdPlaceholder,
        androidProjectId:
          this.projectId != ""
            ? this.projectId
            : this.androidProjectIdPlaceholder,
        iosProjectId:
          this.projectId != ""
            ? this.projectId
            : this.iosProjectIdPlaceholder,
        views: this.views
      };

      this.SET_PACKAGE_NAME(this.appNameInCode);

      // console.log(
      //   "Generate app template with: " + JSON.stringify(templateInfo)
      // );

      // Call the function to start generating the app
      this.generateAppTemplate(templateInfo);

      // navigate to the generation view where we can download the code
      this.$router.push("generate-app");
    },
    toggleShowForm() {
      this.showViewForm = !this.showViewForm;
    },
    removeView(index) {
      this.REMOVE_VIEW(index);
    },
    ...mapActions(["generateAppTemplate", "setFooterState"]),
    ...mapMutations([
      "IS_VIEW_SELECTED",
      "SET_PACKAGE_NAME",
      "REMOVE_VIEW",
      "SET_SELECTED_APP_VIEW",
      "CLEAR_SELECTED_VIEW"
    ])
  },
  data() {
    return {
      showViewForm: true,
      appName: "",
      projectId: "",
      appNameInCode: "",
      selectedFramework: this.getSelectedTechnologyName,
      selectedArchitecture: this.getSelectedArchitectureName,
      selectedView: {}
    };
  }
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/styles.scss";

ul,
li {
  margin: 0;
  padding: 0;
  list-style: none;
}

ul {
  position: absolute;
  top: 5px;
  left: 5px;
}

li {
  width: fit-content;
  padding: 3px;
  font-size: 9px;
  border-radius: 3px;
  margin-top: 3px;
  color: $primary-color;
  background-color: $dark-grey-color;
}

.app-details {
  height: 100%;
  display: grid;

  grid-template-columns: auto auto auto;
  grid-template-areas: "details-input-section views-section view-input-section";
}

.details-input-section {
  grid-area: details-input-section;
  // background-color: red;
  box-sizing: border-box;
  max-width: $form-width;

  margin-right: 40px;
}

.views-input-section {
  grid-area: view-input-section;
}

.views-section {
  grid-area: views-section;
  // display: grid;
  height: 100%;
  max-height: 75vh;
}

.no-views-message {
  margin-top: 100px;
  font-weight: 900;
  font-size: 25px;

  color: $title-additional-color;
}

.phone-representation {
  background-color: $light-grey-color;
  width: 120px;
  height: 200px;
  margin-left: 15px;
  border-radius: 5px;
  box-sizing: border-box;
  position: relative;
  border: solid 2px transparent;
  transition: 0.3s;

  $close-button-margin: 5px;
  $close-button-size: 20px;

  .close-button {
    z-index: 100;
    padding: 5px;
    position: absolute;
    right: 0;
    width: $close-button-size;
    height: $close-button-size;
    border-radius: 15px;
    margin-top: $close-button-margin;
    margin-right: $close-button-margin;

    background-color: $info-container-color;
    cursor: pointer;
  }
}

.phone-representation:hover {
  border: solid 2px $very-light-grey-color;
}

.selected-view,
.selected-view:hover {
  border: solid 2px $primary-color;
}

.view-representation {
  width: 150px;
  height: 250px;
  box-sizing: border-box;
  cursor: pointer;

  .view-name {
    text-align: center;
    margin: 0;
    padding: 0;
    margin-top: 3px;
    margin-bottom: 0;
  }

  .view-type {
    font-weight: 400;
    font-size: 11px;
    text-align: center;
  }
}

.app-views {
  display: grid;

  grid-template-columns: repeat(auto-fit, 200px);
  grid-row-gap: 20px;

  margin-top: 50px;

  box-sizing: border-box;
}

.app-view-simplebar {
  height: calc(100% - 60px);
  width: 100%;
}

.inline-button {
  width: 90px;
  padding: 5px 10px;
  font-size: 14px;
  font-weight: 800;
  background-color: $primary-color;
  border-radius: 5px;
  text-align: center;
  margin-left: 200px;

  display: inline-block;
  cursor: pointer;
}

.input-form {
  height: 70vh;
}

.extra-space {
  height: 130px;
}
</style>
